import { Suspense, useEffect, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import DefaultLayout from "./layouts/default-layout";
import ErrorPageLayout from "./layouts/error-layout";
import Home from "./pages/home/home";
import NotFound from "./pages/not-found";
import { ScrollProvider } from "./context/scroll-context";
import { AppProvider } from "./context/app-context";
import ServiceBaseLayout from "./pages/services/subpages/base-layout";
import ProductBaseLayout from "./pages/products/subpages/base-layout";
import PortfolioBaseLayout from "./pages/portfolio/subpages/base-layout";
import { hireTeamUrlRouting } from "./config/hire-team-content-config";
import LoadingScreen from "./components/loader/loading-screen";
import { portfolioPages } from "./config/portfolio-content-config";
import { serviceUrlRouting } from "./config/services-config";
import { productUrlRouting } from "./config/product-content-config";
import Portfolio from "./pages/portfolio/portfolio";
import CaseStudies from "./pages/case-studies/case-studies";
import AboutUs from "./pages/company/subpages/pages/about-us";
import JoinUs from "./pages/company/subpages/pages/join-us";
import ContactUs from "./pages/company/subpages/pages/contact-us";
import CaseStudyBaseLayout from "./pages/case-studies/subpages/base-layout";
import DevelopmentCenter from "./pages/company/subpages/pages/development-center";
import ServicesHome from "./pages/services/services-home";
import BlogComponent from "./pages/blog/blog-home";
import BlogLayout from "./pages/blog/blog-layout";
import AllPosts from "./pages/blog/all-posts";
import CategoryPage from "./pages/blog/category-page";

const HireTeamBaseLayout = lazy(() =>
  import("./pages/hire-developers/subpages/base-layout")
);

function App() {
  const location = useLocation();
  useEffect(() => {
    history.scrollRestoration = "manual";
    if (location.pathname !== "/company/about_us/our-team") {
      window.scrollTo(0, 0);
    }

    // Listen for the "popstate" event to handle back navigation
    const handlePopState = () => {
      window.scrollTo(0, 0);
    };
    window.addEventListener("popstate", handlePopState);
    // Clean up the event listener and scroll restoration on component unmount
    return () => {
      window.removeEventListener("popstate", handlePopState);
      history.scrollRestoration = "auto";
    };
  }, [location.pathname]);
  return (
    <>
      <ScrollProvider>
        <AppProvider>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route element={<DefaultLayout />}>
                <Route exact={true} path="/" element={<Home />} />
                {/* hireTeam pages */}
                <Route element={<HireTeamBaseLayout />}>
                  {hireTeamUrlRouting.map(({ pathname }) => (
                    <Route key={pathname} path={pathname} element />
                  ))}
                </Route>
                {/* service pages */}
                <Route
                  path="/services"
                  exact={true}
                  element={<ServicesHome />}
                />
                <Route element={<ServiceBaseLayout />}>
                  {serviceUrlRouting.map(({ pathname }) => (
                    <Route key={pathname} path={pathname} element />
                  ))}
                </Route>
                {/* product pages */}

                <Route element={<ProductBaseLayout />}>
                  {productUrlRouting.map(({ pathname }) => (
                    <Route key={pathname} path={pathname} element />
                  ))}
                </Route>
                {/* resources pages */}
                <Route
                  exact={true}
                  path="resources/portfolio"
                  element={<Portfolio />}
                />
                <Route element={<PortfolioBaseLayout />}>
                  {portfolioPages.map((pageName) => (
                    <Route
                      key={pageName}
                      path={`resources/portfolio/detail/${pageName}`}
                      element={<PortfolioBaseLayout pageName={pageName} />}
                    />
                  ))}
                </Route>
                {/* case study pages */}
                <Route
                  exact={true}
                  path="resources/case_study"
                  element={<CaseStudies />}
                />
                <Route
                  exact={true}
                  path="resources/iot_case_study"
                  element={<CaseStudyBaseLayout />}
                />
                <Route
                  exact={true}
                  path="resources/ai_case_study"
                  element={<CaseStudyBaseLayout />}
                />
                <Route
                  exact={true}
                  path="resources/rpa_case_study"
                  element={<CaseStudyBaseLayout />}
                />
                <Route
                  exact={true}
                  path="resources/cloud_case_study"
                  element={<CaseStudyBaseLayout />}
                />
                <Route
                  exact={true}
                  path="resources/crossplatform_case_study"
                  element={<CaseStudyBaseLayout />}
                />
                <Route
                  exact={true}
                  path="resources/emr_case_study"
                  element={<CaseStudyBaseLayout />}
                />
                <Route
                  exact={true}
                  path="resources/ocr_case_study"
                  element={<CaseStudyBaseLayout />}
                />
                <Route
                  exact={true}
                  path="resources/blockchain_case_study"
                  element={<CaseStudyBaseLayout />}
                />
                <Route
                  exact={true}
                  path="resources/android_case_study"
                  element={<CaseStudyBaseLayout />}
                />
                <Route
                  exact={true}
                  path="resources/article"
                  element={<BlogComponent />}
                />
                <Route
                  exact={true}
                  path="resources/article/:slug"
                  element={<BlogLayout />}
                />
                <Route
                  path="resources/article/viewall/:type"
                  element={<AllPosts />}
                />
                <Route
                  path="resources/article/category/:category"
                  element={<CategoryPage />}
                />
                {/* company pages */}
                <Route
                  exact={true}
                  path="/company/about_us"
                  element={<AboutUs />}
                />
                <Route
                  exact={true}
                  path="company/join_us"
                  element={<JoinUs />}
                />
                <Route
                  exact={true}
                  path="company/development_center"
                  element={<DevelopmentCenter />}
                />
                <Route
                  exact={true}
                  path="company/about_us/our-team"
                  element={<AboutUs />}
                />
                <Route
                  exact={true}
                  path="company/contact_us"
                  element={<ContactUs />}
                />
              </Route>

              <Route element={<ErrorPageLayout />}>
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </AppProvider>
      </ScrollProvider>
    </>
  );
}

export default App;
